import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./AppShell";
import { LoginRoute, RequireAuth, RequireManagement, RequireStaff } from "./AuthRoutes";
import { PlaceholderPage } from "./PlaceholderPage";
import { LoginPage } from "../features/auth/LoginPage";
import { StaffApp } from "../features/staff/StaffApp";
import {
  AccountLayout,
  AccountProfilePage,
  AccountSettingsPage,
} from "../account/AccountPages";
import { AccountSecurityPage } from "../account/AccountSecurityPage";
import { AttentionPage } from "../features/attention/AttentionPage";
import { EventWorkspace } from "../features/events/EventWorkspace";
import { EventsListPage } from "../features/events/EventsListPage";
import {
  InvoiceImportBatchPage,
  InvoiceImportReviewPage,
  InvoiceImportRoutes,
} from "../features/invoice-import/InvoiceImportPage";
import { EventOverview } from "../features/events/EventOverview";
import { EventSection } from "../features/events/EventSection";
import { EventWork } from "../features/events/EventWork";
import { TodayScreen } from "../features/today/TodayScreen";
import { OperationsProvider, useOperations } from "../state/OperationsProvider";
import { useAuth } from "../state/AuthProvider";
import { ActiveViewPersistence } from "../auth/ActiveViewPersistence";

function ManagementRoutes() {
  const { snapshot } = useOperations();
  if (!snapshot) {
    return (
      <div className="placeholder-view">
        <div><span>Operations</span><h2>Loading</h2><p>Loading the operations snapshot.</p></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Navigate to="/today" replace />} />
        <Route path="/today" element={<TodayScreen />} />
        <Route path="/look-ahead" element={<PlaceholderPage eyebrow="Planning surface" title="Look Ahead" detail="Future Event readiness and efficient advance-work grouping will live here." />} />
        <Route path="/events" element={<EventsListPage />} />
        <Route path="/events/import" element={<InvoiceImportRoutes />}>
          <Route index element={<InvoiceImportBatchPage />} />
          <Route path="review/:itemId" element={<InvoiceImportReviewPage />} />
        </Route>
        <Route path="/events/:eventId" element={<EventWorkspace />}>
          <Route index element={<EventOverview />} />
          <Route path="flow" element={<EventSection surface="flow" />} />
          <Route path="commitments" element={<EventSection surface="commitments" />} />
          <Route path="planning" element={<EventSection surface="planning" />} />
          <Route path="requirements" element={<EventSection surface="requirements" />} />
          <Route path="work" element={<EventWork />} />
          <Route path="conditions" element={<EventSection surface="conditions" />} />
        </Route>
        <Route path="/attention" element={<AttentionPage />} />
        <Route path="/horizon" element={<PlaceholderPage eyebrow="Capacity planning" title="Horizon" detail="Long-range demand, staffing, resources, and operational risk will be visible here." />} />
        <Route path="/more" element={<PlaceholderPage eyebrow="Workspace" title="More" detail="Look Ahead and Horizon are available from here on mobile. Account and role tools will follow later." />} />
        <Route path="*" element={<PlaceholderPage eyebrow="Not found" title="Page not found" detail="That route is not part of FSWL Ops yet." />} />
      </Route>
    </Routes>
  );
}

export function App() {
  const { status } = useAuth();

  if (status === "checking") {
    return (
      <div className="auth-loading-screen">
        <p className="muted">Checking session…</p>
      </div>
    );
  }

  return (
    <>
      <ActiveViewPersistence />
      <Routes>
      <Route element={<LoginRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route path="/account" element={<AccountLayout />}>
          <Route index element={<Navigate to="/account/profile" replace />} />
          <Route path="profile" element={<AccountProfilePage />} />
          <Route path="settings" element={<AccountSettingsPage />} />
          <Route path="security" element={<AccountSecurityPage />} />
        </Route>

        <Route element={<RequireStaff />}>
          <Route path="/staff/*" element={<StaffApp />} />
        </Route>

        <Route element={<RequireManagement />}>
          <Route
            path="/*"
            element={
              <OperationsProvider>
                <ManagementRoutes />
              </OperationsProvider>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}
